/**
 * I add or log an expense with editable, receipt-aware suggestions. Three entries,
 * one sheet: type it, scan a receipt, or scan a quote. AI pre-fills title,
 * amount, category and the likely bank; the final question is always the same:
 * just this month, or recurring? Camera capture works on mobile web via the
 * file input's `capture` attribute.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useRef, useState, useEffect } from 'react';
import PinPreview from './PinPreview.jsx';
import VoiceButton from './VoiceButton.jsx';
import { api } from '../api.js';
import CategoryPicker from './CategoryPicker.jsx';
import FormulaOptions from './FormulaOptions.jsx';
import { AskEnableAI, useAI } from './AIGate.jsx';
import { fmtWhole, moneySymbol } from '../money.js';

export default function AddExpense({ budget, banks, categories, onClose, onSaved }) {
  const camRef = useRef();
  const upRef = useRef();   // upload an existing/paper receipt photo (no forced camera)
  // The formula box before the first suggested chip was picked (null = no pick yet), for Undo.
  const formulaBefore = useRef(null);
  const [kind, setKind] = useState('expense');   // expense | income — one sheet, both directions
  const [form, setForm] = useState({ title: '', amount: '', bank: '', category: '',
                                     description: '', completed: true,
                                     tax_deductible: false, tax_bucket: '' });
  const [recurring, setRecurring] = useState(false);
  const [advRepeat, setAdvRepeat] = useState(false);   // "Advanced repeat" disclosure (recurring only)
  const [cadence, setCadence] = useState('none');       // none | daily | weekly | monthly
  const [unitRate, setUnitRate] = useState('');         // R per unit for a cadence budget
  const [timesPerDay, setTimesPerDay] = useState('1');  // daily: units per day
  const [dow, setDow] = useState([]);                   // weekly: ISO weekdays 1..7
  const [expectedDay, setExpectedDay] = useState('');   // monthly: day of month (optional)
  // Live preview of what the cadence produces this month — mirrors the mobile
  // recurring editor so the plan is legible before it is saved.
  const repeatSummary = () => {
    const rate = Number(unitRate) || 0;
    const r = (x) => fmtWhole(x);
    if (cadence === 'none') return 'Copied into every new month as-is.';
    if (!rate) return `Enter the ${moneySymbol()} per unit to preview the monthly total.`;
    if (cadence === 'daily') { const t = Number(timesPerDay) || 1; return `${t}\u00d7 a day \u2192 about ${t * 31} units of ${r(rate)} a month.`; }
    if (cadence === 'weekly') { const pk = dow.length; return pk ? `${pk} day(s) a week \u2192 about ${Math.round(pk * 4.43)} units of ${r(rate)} a month.` : 'Pick at least one weekday.'; }
    if (cadence === 'monthly') return `Once a month${expectedDay ? ` on the ${expectedDay}` : ''} \u2192 1 unit of ${r(rate)}.`;
    return '';
  };
  const [isSalary, setIsSalary] = useState(false);
  const [scanMode, setScanMode] = useState('receipt');
  const [scanning, setScanning] = useState(false);
  const ai = useAI();   // AI master switch — scan buttons hide or disable while off
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [vvh, setVvh] = useState(null);   // visible-viewport height so the sheet stays above the mobile keyboard
  const [receiptFile, setReceiptFile] = useState(null);
  const [aiCat, setAiCat] = useState(false);   // category was AI-suggested
  const [fromMaster, setFromMaster] = useState(true);
  const [savingsKind, setSavingsKind] = useState('deposit');
  const [target, setTarget] = useState(null);   // existing expense to apply to
  const [tq, setTq] = useState('');             // type-to-search existing expenses
  const [tSpend, setTSpend] = useState(true);   // count as spend on the target
  const [mergeDismiss, setMergeDismiss] = useState(''); // a typed title the user chose to keep separate
  const [advOpts, setAdvOpts] = useState(false);   // 🎯 advanced: edit the formula
  const [formulaEdit, setFormulaEdit] = useState('');
  const [tReplace, setTReplace] = useState(false); // replace total & tick done
  const [amtMode, setAmtMode] = useState('auto'); // auto | money | units
  const [rnBump, setRnBump] = useState(0);      // live − / + paid-unit taps here
  const [ovInput, setOvInput] = useState('');   // #314 set-total-spent override input
  const [ovBusy, setOvBusy] = useState(false);
  // Optional: link this NEW expense to a shop at creation time.
  const [shopOpen, setShopOpen] = useState(false);
  const [shopQ, setShopQ] = useState('');
  const [shopResults, setShopResults] = useState([]);
  const [savedPlaces, setSavedPlaces] = useState([]);
  const [place, setPlace] = useState(null);     // chosen {id?, name, address, lat, lng}
  const [shopBusy, setShopBusy] = useState(false);
  const [pos, setPos] = useState(null);         // live position → nearest-first

  useEffect(() => {
    // Preload saved places so the add-to-existing location row can name the
    // expense's current shop before the picker is opened. Best-effort.
    api('/budget/places/').then((d) =>
      setSavedPlaces(Array.isArray(d) ? d : (d.results || []))).catch(() => {});
  }, []);

  // VERY SMART amounts: against a rate×count formula, what you type is READ.
  // "2" (small, below the unit) looks like a UNIT COUNT; "74" against a
  // 37-unit looks like WHOLE UNITS; anything else is plain money. Vault
  // offers the reading as a choice — never a rule.
  const tItem = target && (budget.items || []).find((x) => x.id === target);
  // Adding a NEW expense: if its name already matches one you have, offer to
  // MERGE into that line and set it active — so you never end up with two
  // "Groceries". Exact (normalised) match only, so it never nags on near-misses.
  const mergeSuggest = (kind === 'expense' && !target && form.title.trim().length >= 3
      && mergeDismiss !== form.title.trim().toLowerCase())
    ? (budget.items || []).find((x) => x.kind === 'expense' && x.source !== 'transfer'
        && (x.title || '').toLowerCase().trim() === form.title.trim().toLowerCase())
    : null;
  const chooseMerge = async () => {
    if (!mergeSuggest) return;
    // "Set active": a done expense re-opens so the merged spend lands on the live line.
    if (mergeSuggest.completed) {
      try { await api(`/budget/items/${mergeSuggest.id}/complete/`, { method: 'POST', body: { completed: false } }); }
      catch { /* non-fatal — still link to it */ }
    }
    setTarget(mergeSuggest.id); setTq(''); setRnBump(0);
  };
  const fMatch = tItem && String(tItem.calculation || '').replace(/\s+/g, '')
    .match(/^([0-9.]+)\*([0-9]+)(.*)$/);
  const unit = fMatch ? Number(fMatch[1]) : 0;
  const amtN = Number((() => {
    let a = String(form.amount || '').replace(/[rR\s]/g, '');
    if (a.includes(',') && !a.includes('.')) a = a.replace(',', '.');
    return a.replace(/,/g, '');
  })());
  const looksLikeCount = unit > 0 && Number.isInteger(amtN) && amtN > 0
    && amtN <= 60 && amtN < unit;
  const looksLikeUnits = unit > 0 && amtN >= unit
    && Math.abs(amtN / unit - Math.round(amtN / unit)) < 1e-9;
  // With a FORMULA target the choice is ALWAYS offered (units / money chips).
  // Auto default is MONEY — a plain amount is money unless it's an EXACT
  // multiple of the rate (74 → 2×R37), which is an unambiguous unit signal.
  // A small number like 34 (below the R37 rate) is NOT auto-read as 34 units
  // anymore — that turned a R34 spend into R1258. Tap UNITS to log units.
  const effMode = !(unit > 0) ? 'money'
    : amtMode === 'auto' ? (looksLikeUnits ? 'units' : 'money') : amtMode;
  // What the number MEANS as units — exact multiples convert (74 → 2×37),
  // anything else IS the count (2 → 2 units).
  const prospUnits = !(unit > 0) ? 0
    : looksLikeUnits ? Math.round(amtN / unit)
    : Math.max(1, Math.round(amtN || 1));
  const unitsCount = effMode === 'units' ? prospUnits : 0;
  const moneyVal = effMode === 'units' ? unitsCount * unit : amtN;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Open the shop picker: pull YOUR saved places (to reuse) and grab location
  // so a nearby search ranks closest-first.
  const openShop = async () => {
    setShopOpen(true);
    try {
      const d = await api('/budget/places/');
      setSavedPlaces(Array.isArray(d) ? d : (d.results || []));
    } catch { /* fine — search still works */ }
    navigator.geolocation?.getCurrentPosition(
      (g) => setPos({ lat: g.coords.latitude, lng: g.coords.longitude }),
      () => {}, { timeout: 10000, maximumAge: 600000, enableHighAccuracy: false });
  };
  // Same resolver the map uses: real business names, nearest-first.
  const searchShops = async () => {
    const q = shopQ.trim() || form.title.trim();
    if (!q) { setError('Type a shop name or address to find it.'); return; }
    setShopBusy(true); setError('');
    try {
      const d = await api('/budget/places/resolve/',
                          { method: 'POST',
                            body: { query: q, ...(form.title.trim() ? { title: form.title.trim() } : {}),
                                    ...(pos || {}) } });
      setShopResults(d.candidates || []);
    } catch (e) {
      setError(e.data?.detail || e.message);
    } finally { setShopBusy(false); }
  };
  // Search Google as you type (debounced) so you don't have to press Find — and
  // so the RIGHT shop surfaces even when it isn't one of your saved places.
  useEffect(() => {
    if (!shopOpen) return undefined;
    const q = shopQ.trim();
    if (q.length < 2) return undefined;
    const t = setTimeout(() => { searchShops(); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopQ, shopOpen]);

  const _normAmt = () => {
    let a = String(form.amount || '').replace(/[rR]\s*/g, '').replace(/\s+/g, '')
      .replace(/[x×]/gi, '*');
    if (a.includes(',') && !a.includes('.')) a = a.replace(',', '.');
    else a = a.replace(/,/g, '');
    return a;
  };

  // APPLY TO AN EXISTING expense — grouped together: the amount lands as
  // spend and/or formula units (either or both), and a typed name (optional)
  // leaves a dated sub-line on the master expense's note.
  // 🎯 Advanced: change the expense's FORMULA itself for this month (not a
  // spend — it edits the plan, e.g. 200*31 → 200*35).
  const saveFormula = async () => {
    const calc = String(formulaEdit || '').trim().replace(/[x×]/gi, '*');
    if (!calc) return;
    setBusy(true); setError('');
    try {
      await api(`/budget/items/${target}/set_calc/`,
                { method: 'POST', body: { calculation: calc, scope: 'month' } });
      onSaved();
    } catch (e) { setError(e.data?.detail || e.message); setBusy(false); }
  };

  const applyToExisting = async () => {
    setBusy(true); setError('');
    try {
      const t = (budget.items || []).find((x) => x.id === target);
      if (!t) throw new Error('Pick the expense again — it moved.');
      if (!moneyVal || Number.isNaN(moneyVal)) throw new Error('Enter an amount.');
      if (tReplace) {
        // Replace this expense's spend with EXACTLY the typed amount and settle
        // it (ticked done). Formula counter cleared so spent = the amount.
        await api(`/budget/items/${t.id}/`,
                  { method: 'PATCH',
                    body: { actual: moneyVal.toFixed(2), reduce_n: 0, completed: true } });
        onSaved();
        return;
      }
      if (tSpend) {
        // ONE model: adding spend to an existing expense LOGS a deletable visit
        // (bump:1 → the server applies its units/money AND deducts the bank
        // atomically, no separate PATCH to double it), placeless when the
        // expense has no shop. The sub-name rides along as the visit's note, so
        // the spend reads under the Logged tab with its description.
        const sub = form.title.trim();
        const note = (sub && sub.toLowerCase() !== (t.title || '').toLowerCase())
          ? sub.slice(0, 200) : '';
        const feed = (effMode === 'units' && fMatch)
          ? { units: unitsCount, amount: '0.00' }
          : { units: 0, amount: moneyVal.toFixed(2) };
        // THIS entry can log at a DIFFERENT location than the expense's home —
        // whatever shop/address was picked here, else the expense's own place.
        let visitPlace = t.place || null;
        if (place) {
          visitPlace = place.id || null;
          if (!place.id) {
            try {
              const np = await api('/budget/places/', { method: 'POST', body: {
                name: (place.name || t.title).slice(0, 60), address: place.address || '',
                lat: place.lat, lng: place.lng,
                kind: place.source === 'address' ? 'address' : 'shop' } });
              visitPlace = np.id;
            } catch { visitPlace = t.place || null; }
          }
        }
        const loggedVisit = await api('/budget/place-visits/', {
          method: 'POST',
          body: { place: visitPlace, item: t.id, title: t.title, note,
                  on: new Date().toISOString().slice(0, 10),
                  bump: 1, confirmed: true, ...feed },
        });
        // Attach the scanned/added receipt to THE LOGGED ITEM (the visit), not
        // the whole expense — the proof lives on the logged spend (web + mobile).
        if (receiptFile && loggedVisit && loggedVisit.id) {
          try {
            const rfd = new FormData();
            rfd.append('receipt', receiptFile);
            await api(`/budget/place-visits/${loggedVisit.id}/receipt/`, { method: 'POST', form: rfd });
          } catch { /* keep the spend even if the receipt upload fails */ }
        }
      }
      onSaved();
    } catch (e) {
      setError(e.data?.detail || e.message);
      setBusy(false);
    }
  };

  // Pot movement from the + : POST a savings transaction with the chosen
  // bank — the backend moves that bank's balance together with the pot.
  const saveSavings = async () => {
    setBusy(true); setError('');
    try {
      let amt = String(form.amount || '').replace(/[rR]\s*/g, '').replace(/\s+/g, '');
      if (amt.includes(',') && !amt.includes('.')) amt = amt.replace(',', '.');
      else amt = amt.replace(/,/g, '');
      await api('/budget/savings-transactions/',
                { method: 'POST',
                  body: { kind: savingsKind, amount: amt,
                          note: form.description,
                          ...(form.bank ? { bank: Number(form.bank) } : {}) } });
      onSaved();
    } catch (e) {
      const d = e.data;
      setError(d && typeof d === 'object' && !d.detail
        ? Object.entries(d).map(([k, v]) =>
            `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join(' · ')
        : (d?.detail || e.message));
      setBusy(false);
    }
  };

  // Master pairing choice: shown for income landing on a bank that isn't
  // the ★ master's (default = it moved from the master).
  const masterLine = (budget.items || []).find((i) => i.salary_primary) || null;
  const offMaster = !!(masterLine && form.bank
                       && Number(form.bank) !== Number(masterLine.bank || 0));

  // AI assist while typing: name the item, tab away, and one call fills the
  // rest — category (from YOUR tabs), a short note, the recurring hint, and
  // the likely bank from your history. Every field stays overridable and a
  // failed call just means manual entry; hints never block a save.
  const suggestCategory = async () => {
    if (!form.title.trim()) return;
    try {
      const d = await api('/budget/items/assist/',
                          { method: 'POST',
                            body: { title: form.title.trim(), kind } });
      setForm((f) => ({
        ...f,
        category: f.category || (d.category ? String(d.category) : f.category),
        description: f.description || d.note || f.description,
        bank: f.bank || (d.bank ? String(d.bank) : f.bank),
      }));
      if (d.category && !form.category) setAiCat(true);
      if (d.recurring_hint && !recurring) setRecurring(true);
    } catch { /* hint only — never block logging */ }
  };

  const scan = async (mode) => {
    setScanMode(mode);
    camRef.current?.click();
  };

  /** The saved shop whose name matches the receipt, closest to `fix` (or the first); null when none. */
  const nearestSavedByName = (title, fix) => {
    const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const key = norm(title);
    if (key.length < 3) return null;
    const hits = savedPlaces.filter((pl) => {
      const k = norm(pl.name);
      return k && (k === key || (k.length >= 4 && key.length >= 4 && (k.includes(key) || key.includes(k))));
    });
    if (!hits.length) return null;
    if (fix) {
      const km = (pl) => (pl.lat == null ? 1e9 : Math.hypot((Number(pl.lat) - fix.lat) * 111, (Number(pl.lng) - fix.lng) * 92));
      hits.sort((a, b) => km(a) - km(b));
    }
    const h = hits[0];
    return { id: h.id, name: h.name, address: h.address, lat: h.lat, lng: h.lng };
  };
  /** Read a receipt or quote into editable fields, replacing any earlier scan choices without saving. */
  const scanFile = async (f, mode = scanMode) => {
    if (!f) return;
    setScanning(true); setError('');
    const fd = new FormData();
    fd.append('receipt', f);
    fd.append('mode', mode);
    // #738/#739 — your position lets the server offer the CLOSEST branch by name.
    const fix = pos || await new Promise((res) => navigator.geolocation?.getCurrentPosition(
      (g) => res({ lat: g.coords.latitude, lng: g.coords.longitude }), () => res(null),
      { timeout: 4000, maximumAge: 600000, enableHighAccuracy: false }) || res(null));
    if (fix) { fd.append('lat', String(fix.lat)); fd.append('lng', String(fix.lng)); if (!pos) setPos(fix); }
    try {
      const d = await api('/budget/items/scan_receipt/', { method: 'POST', form: fd });
      if (!d || d.ai_off || d.ai_consent_required) return;
      setForm((prev) => ({
        ...prev,
        title: d.suggested_title || d.title || '',
        amount: d.amount ?? '',
        description: d.description || '',
        bank: d.bank || prev.bank,
        category: d.category ?? '',
        completed: mode === 'receipt',   // a receipt is spent; a quote is planned
      }));
      setRecurring(!!d.recurring_hint);
      // #276 — if the AI matched an EXISTING expense, pre-select it so you apply
      // the receipt to that line (a confirm) instead of creating a duplicate.
      setTarget(d.item ?? null);
      // #156a — the scan already resolved (or auto-pinned) the location; pre-link
      // it so the receipt lands on the Spend Map without picking a place by hand.
      // Where: the server's shop, else your saved shop whose name matches (closest
      // first). "Pick a different shop" always lets you change it.
      setPlace(d.place != null ? { id: d.place, name: d.place_name || d.location || 'Location' } : nearestSavedByName(d.title, fix));
      setReceiptFile(f);
    } catch (err) {
      setError(err.data?.detail || err.message);
    } finally {
      setScanning(false);
    }
  };

  const onPhoto = (e) => { const f = e.target.files?.[0]; if (f) scanFile(f, scanMode); };

  // #318 — paste a receipt image straight from the clipboard (a screenshot, or a
  // photo copied from another app). The button uses clipboard.read(); Ctrl/Cmd+V
  // works too while the sheet is open. Always scans as a receipt.
  const pasteReceipt = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const type = it.types.find((t) => t.startsWith('image/'));
        if (type) {
          const blob = await it.getType(type);
          scanFile(new File([blob], 'pasted-receipt.png', { type: blob.type }), 'receipt');
          return;
        }
      }
      setError('No image on the clipboard — copy a receipt image first.');
    } catch {
      setError('Clipboard paste needs permission — or copy an image and press Ctrl/Cmd+V here.');
    }
  };

  useEffect(() => {
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'));
      if (item) { const f = item.getAsFile(); if (f) scanFile(f, 'receipt'); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode, budget]);

  const save = async () => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('budget', budget.id);
      fd.append('kind', kind);
      if (kind === 'income' && isSalary) fd.append('is_salary', 'true');
      fd.append('title', form.title);
      // Amounts arrive human-shaped — "R 1 000,00", "1,000.50", "36x5" —
      // normalise before the server sees them: strip currency/spaces,
      // SA decimal comma becomes a dot, × becomes *.
      let amt = String(form.amount || '0').replace(/[rR]\s*/g, '')
        .replace(/\s+/g, '').replace(/[x×]/gi, '*');
      if (amt.includes(',') && !amt.includes('.')) amt = amt.replace(',', '.');
      else amt = amt.replace(/,/g, '');
      if (/[+*/-]/.test(amt.slice(1))) {          // formula → evaluated server-side
        fd.append('calculation', amt);
        fd.append('amount', '0');
      } else {
        fd.append('amount', amt);
      }
      fd.append('description', form.description);
      fd.append('completed', form.completed ? 'true' : 'false');
      if (kind === 'income' && offMaster) {
        fd.append('from_master', fromMaster ? 'true' : 'false');
      }
      if (form.bank) fd.append('bank', form.bank);
      if (form.category) fd.append('category', form.category);
      if (recurring) fd.append('recurring', 'true');
      if (recurring && kind === 'expense' && cadence !== 'none') {
        fd.append('cadence', cadence);
        if (unitRate !== '') fd.append('unit_rate', String(unitRate));
        if (cadence === 'daily') fd.append('times_per_day', String(Number(timesPerDay) || 1));
        if (cadence === 'weekly') fd.append('days_of_week', dow.slice().sort((a, b) => a - b).join(','));
        if (cadence === 'monthly' && expectedDay !== '') fd.append('expected_day', String(Number(expectedDay) || ''));
      }
      if (kind === 'expense' && form.tax_deductible) {
        fd.append('tax_deductible', 'true');
        if (form.tax_bucket) fd.append('tax_bucket', form.tax_bucket);
      }
      if (receiptFile) fd.append('receipt', receiptFile);
      // Optional shop link: create the pin (if it's a new candidate) then attach
      // it, so the new expense shows on the Spend Map and can drive near-shop
      // reminders. A pin failure never blocks saving the expense.
      let placeId = place?.id || null;
      if (place && !placeId) {
        try {
          const np = await api('/budget/places/',
                               { method: 'POST',
                                 body: { name: (place.name || form.title).slice(0, 60),
                                         address: place.address || '',
                                         lat: place.lat, lng: place.lng,
                                         kind: place.source === 'address' ? 'address' : 'shop' } });
          placeId = np.id;
        } catch { /* keep the expense; skip the pin */ }
      }
      if (placeId) fd.append('place', placeId);
      const created = await api('/budget/items/', { method: 'POST', form: fd });
      // Belt-and-suspenders: guarantee the shop link even if the multipart
      // create didn't carry `place` through, so the expense shows under the pin.
      if (placeId && created?.id && String(created.place) !== String(placeId)) {
        await api(`/budget/items/${created.id}/`,
                  { method: 'PATCH', body: { place: placeId } }).catch(() => {});
      }
      onSaved();
    } catch (e) {
      // SAY which field the server rejected — 'HTTP 400' alone helps no one.
      const d = e.data;
      setError(d && typeof d === 'object' && !d.detail
        ? Object.entries(d).map(([k, v]) =>
            `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join(' · ')
        : (d?.detail || e.message));
      setBusy(false);
    }
  };

  // Keep the sheet inside the VISIBLE viewport: when the mobile browser keyboard
  // opens, window.visualViewport shrinks — track it so the location/shop search
  // results stay above the keyboard instead of hiding behind it (Pass 2 #7).
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const on = () => setVvh(vv.height);
    on();
    vv.addEventListener('resize', on);
    vv.addEventListener('scroll', on);
    return () => { vv.removeEventListener('resize', on); vv.removeEventListener('scroll', on); };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-end md:place-items-center z-50"
         data-guide-context="add-sheet"
         onClick={onClose}>
      <div className="bg-panel border border-edge rounded-t-2xl md:rounded-2xl w-full md:max-w-lg
                      max-h-[92vh] overflow-y-auto p-6"
           style={vvh ? { maxHeight: Math.round(vvh) - 16 } : undefined}
           onClick={(e) => e.stopPropagation()}>
        <h2 className="font-bold text-lg">Log {kind === 'income' ? 'income' : 'an expense'}</h2>

        <div className="mt-3 flex gap-2" data-guide="add-kind">
          {['expense', 'income', 'savings'].map((k) => (
            <button key={k} data-guide={`add-kind-${k}`}
                    className={`flex-1 py-2 rounded-xl border text-sm transition ${
                      kind === k ? (k === 'income' ? 'border-emerald-400 bg-emerald-400/10'
                                    : k === 'savings' ? 'border-vpurple bg-vpurple/15'
                                    : 'border-vblue bg-vblue/15')
                                 : 'border-edge text-slate-400'}`}
                    onClick={() => setKind(k)}>
              {k === 'income' ? 'Income' : k === 'savings' ? '🐷 Savings' : 'Expense'}
            </button>
          ))}
        </div>

        {kind === 'savings' && (
          /* Pot movements straight from the + : a deposit leaves the chosen
             bank into the pot, a withdrawal returns to it. Real money both
             ways — the bank's balance moves with the pot. */
          <div className="mt-4 grid gap-3">
            <div className="flex gap-2">
              {[['deposit', '💰 Deposit into the pot'],
                ['withdrawal', '💸 Withdraw from the pot']].map(([k, label]) => (
                <button key={k}
                        className={`flex-1 py-2 rounded-xl border text-sm ${
                          savingsKind === k ? 'border-vpurple bg-vpurple/10 text-white'
                                            : 'border-edge text-slate-400'}`}
                        onClick={() => setSavingsKind(k)}>
                  {label}
                </button>
              ))}
            </div>
            <input className="input text-right font-medium" placeholder="Amount" data-guide="add-savings-amount"
                   value={form.amount} onChange={(e) => set('amount', e.target.value)} />
            <select className="input text-sm" value={form.bank}
                    title={savingsKind === 'deposit'
                      ? 'The bank the money LEAVES (empty = your savings bank)'
                      : 'The bank the money RETURNS TO (empty = your savings bank)'}
                    onChange={(e) => set('bank', e.target.value)}>
              <option value="">Savings bank (default)</option>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name}</option>)}
            </select>
            <input className="input text-sm" placeholder="Note (optional)"
                   value={form.description}
                   onChange={(e) => set('description', e.target.value)} />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button className="btn-primary" disabled={busy || !form.amount}
                    onClick={saveSavings}>
              {busy ? 'Saving…'
                    : savingsKind === 'deposit' ? 'Deposit' : 'Withdraw'}
            </button>
            <button className="text-sm text-slate-500" onClick={onClose}>Cancel</button>
          </div>
        )}


        {ai.disabled && kind === 'expense' && (
          <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
            Receipt and quote scanning use AI, which is switched off. <AskEnableAI feature="receipt scanning" />
          </div>
        )}
        <div className={`mt-4 grid grid-cols-2 gap-3 ${kind !== 'expense' || !ai.on ? 'hidden' : ''}`}>
          <button className="border border-edge rounded-xl py-3 hover:border-vblue transition"
                  data-guide="add-scan" onClick={() => scan('receipt')} disabled={scanning}>
            {scanning && scanMode === 'receipt' ? 'Reading…' : '📷 Scan a receipt'}
          </button>
          <button className="border border-edge rounded-xl py-3 hover:border-vpurple transition"
                  onClick={() => scan('quote')} disabled={scanning}>
            {scanning && scanMode === 'quote' ? 'Reading…' : '📄 Scan a quote'}
          </button>
          <button className="col-span-2 border border-edge rounded-xl py-2.5 text-sm text-slate-300 hover:border-vblue transition"
                  onClick={() => { setScanMode('receipt'); upRef.current?.click(); }} disabled={scanning}>
            {scanning ? 'Reading...' : 'Upload a receipt you already have'}
          </button>
          <button className="col-span-2 border border-edge rounded-xl py-2.5 text-sm text-slate-300 hover:border-vpurple transition"
                  onClick={pasteReceipt} disabled={scanning}>
            {scanning ? 'Reading...' : '📋 Paste a receipt (Ctrl/Cmd+V)'}
          </button>
          <input ref={camRef} type="file" accept="image/*" capture="environment"
                 className="hidden" onChange={onPhoto} />
          <input ref={upRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onPhoto} />
        </div>

        <div className={`mt-4 grid gap-3 ${kind === 'savings' ? 'hidden' : ''}`}>
          <input className="input" data-guide="add-title"
                 placeholder={kind === 'income' ? 'What is it? (e.g. Salary, Side gig)'
                   : target ? 'Name it (optional — noted on the expense)'
                            : 'What was it? (e.g. Woolworths)'}
                 value={form.title} onChange={(e) => set('title', e.target.value)}
                 onBlur={suggestCategory} />
          {/* Guide hook: the driven tour clicks this (via JS) to fire
              the real AI assist, so the tour SHOWS the note, category and
              bank filling live. Hidden from users; never in the tab order. */}
          <button type="button" data-guide="add-ai-run" onClick={suggestCategory}
                  tabIndex={-1} aria-hidden="true" className="hidden" />

          {mergeSuggest && (
            <div className="border border-vpurple/50 bg-vpurple/10 rounded-xl p-2.5 text-sm"
                 data-guide="add-merge">
              <p>You already have <b>{mergeSuggest.title}</b>
                {mergeSuggest.completed ? ' (marked done)' : ''}. Merge this into it?</p>
              <div className="flex items-center gap-3 mt-2">
                <button className="btn-primary !py-1 !px-3 text-sm" onClick={chooseMerge}>
                  Merge &amp; set active
                </button>
                <button className="text-xs text-slate-500 hover:text-slate-300"
                        onClick={() => setMergeDismiss(form.title.trim().toLowerCase())}>
                  Keep separate
                </button>
              </div>
            </div>
          )}

          {kind === 'expense' && (
            /* WHERE it lands is a choice: its own NEW expense, or grouped
               into an existing one — searchable, selectable — where the
               amount applies as spend and/or formula units. The name turns
               optional and rides along as a sub-line on the master. */
            <div className="border border-edge rounded-xl p-2.5 grid gap-1.5">
              {target ? (() => {
                const t = (budget.items || []).find((x) => x.id === target);
                return (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex-1 truncate">
                        → <span className="font-medium">{t?.title || '…'}</span>
                        <span className="text-[11px] text-slate-500"> — grouped into this expense</span>
                      </span>
                      <button className="w-6 h-6 rounded-full border border-edge text-slate-400
                                         hover:text-white text-xs"
                              title="Back to its own new expense"
                              onClick={() => { setTarget(null); setRnBump(0); setTReplace(false); setAdvOpts(false); setFormulaEdit(''); }}>✕</button>
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-vblue">📝 Basic — apply the spend</div>
                    <div className={`flex gap-1.5 ${tReplace ? 'opacity-40 pointer-events-none' : ''}`}>
                      <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                                tSpend ? 'border-vblue text-vblue bg-vblue/10'
                                       : 'border-edge text-slate-500'}`}
                              onClick={() => setTSpend(!tSpend)}>
                        {tSpend ? '✓ ' : ''}
                        {effMode === 'units' ? 'mark units PAID' : 'count as spend'}
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-600">
                      {effMode === 'units' ? 'Marks units paid on the existing budget.'
                                           : "Adds the money to what's spent."}{' '}Or Replace &amp; tick below.
                    </p>
                    <button className={`w-full text-xs py-1.5 rounded-xl border mt-1 ${
                              tReplace ? 'border-emerald-400 text-emerald-400 bg-emerald-400/10'
                                       : 'border-edge text-slate-500'}`}
                            onClick={() => setTReplace(!tReplace)}>
                      {tReplace ? '✓ ' : ''}Replace total &amp; mark done
                    </button>
                    {tReplace && (
                      <p className="text-[10px] text-emerald-400/80">
                        Sets spent to exactly this amount and ticks the expense done
                        (overrides the Basic options).
                      </p>
                    )}
                    {t?.calculation ? (
                      <>
                        <button type="button"
                                className="text-[10px] uppercase tracking-wide text-vpurple text-left mt-1"
                                onClick={() => { const n = !advOpts; setAdvOpts(n); if (n && !formulaEdit) setFormulaEdit(t.calculation || ''); }}>
                          {advOpts ? '▾' : '▸'} 🎯 Advanced — edit the formula itself
                        </button>
                        {advOpts && (
                          <div className="grid gap-1">
                            <input className="input !py-1.5 text-sm"
                                   placeholder={t.calculation}
                                   value={formulaEdit}
                                   onChange={(e) => setFormulaEdit(e.target.value)} />
                            {/* Several options counted on THIS month's real days and
                                weekdays (#67); a chip fills the formula box, never saves. */}
                            <FormulaOptions endpoint="/budget/items/suggest_formula/"
                                    disabled={busy}
                                    body={{ title: t.title || '', amount: t.amount, item: t.id, month: budget.month }}
                                    onPick={(res) => { if (formulaBefore.current === null) formulaBefore.current = formulaEdit; setFormulaEdit(res); }}
                                    onUndo={() => { setFormulaEdit(formulaBefore.current ?? ''); formulaBefore.current = null; }} />
                            <button type="button"
                                    className="text-xs py-1.5 rounded-xl border border-vpurple text-vpurple bg-vpurple/10 disabled:opacity-40"
                                    disabled={busy || !formulaEdit.trim()}
                                    onClick={saveFormula}>
                              Save formula ({t.calculation} → {formulaEdit.trim() || '…'})
                            </button>
                            <p className="text-[10px] text-slate-600">
                              Changes the budget's formula for this month (e.g.
                              200*31 → 200*35). This edits the PLAN, not a spend.
                            </p>
                          </div>
                        )}
                      </>
                    ) : null}
                    {unit > 0 && (() => {
                      /* LIVE − / + right here: each tap pays (or un-pays) one
                         unit on the target immediately — no save needed. */
                      const curN = (Number(tItem.reduce_n) || 0) + rnBump;
                      const curSpent = (Number(tItem.actual) || 0) + curN * unit;
                      const bumpPaid = async (d) => {
                        // Pay / un-pay one unit through the canonical primitive so it
                        // logs a deletable visit under Logged (#292), never a bare
                        // reduce_n PATCH that recorded nothing.
                        if (d < 0 && curN <= 0) return;
                        try {
                          if (d > 0) {
                            await api(`/budget/items/${tItem.id}/log/`,
                                      { method: 'POST', body: { units: 1, place: tItem.place || null } });
                          } else {
                            await api(`/budget/items/${tItem.id}/unlog/`,
                                      { method: 'POST', body: { kind: 'unit' } });
                          }
                          setRnBump(rnBump + (d > 0 ? 1 : -1));
                        } catch (e) {
                          setError(e.data?.detail || e.message);
                        }
                      };
                      return (
                        <div className="flex items-center gap-2 text-xs text-slate-500
                                        border border-edge rounded-xl px-2.5 py-1.5">
                          <span className="flex-1">
                            already on it: {moneySymbol()}{curSpent.toFixed(2)} spent · {curN}× paid
                          </span>
                          <button className="w-7 h-7 rounded-full border border-edge text-sm
                                             leading-none hover:border-vblue hover:text-vblue
                                             disabled:opacity-30"
                                  title="Undo one paid unit — applies immediately"
                                  disabled={curN <= 0}
                                  onClick={() => bumpPaid(-1)}>−</button>
                          <button className="w-7 h-7 rounded-full border border-edge text-sm
                                             leading-none hover:border-vblue hover:text-vblue"
                                  title={`Pay one unit of R${unit} — applies immediately`}
                                  onClick={() => bumpPaid(+1)}>+</button>
                        </div>
                      );
                    })()}
                    {tItem && (() => {
                      /* #314/#315 — set the TOTAL actually spent, overriding the
                         formula AND logged spend so the budget leftover is right.
                         Obvious + reversible: a badge shows when it is overriding. */
                      const ov = tItem.spent_override;
                      const isOv = ov !== null && ov !== undefined;
                      const setSpent = async (clear) => {
                        setOvBusy(true);
                        try {
                          await api(`/budget/items/${tItem.id}/set_spent/`, { method: 'POST',
                            body: clear ? { clear: true } : { amount: Number(ovInput) } });
                          setOvInput('');
                          onSaved();
                        } catch (e) { setError(e.data?.detail || e.message); }
                        finally { setOvBusy(false); }
                      };
                      return (
                        <div className="grid gap-1 border border-edge rounded-xl px-2.5 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-slate-400 flex-1">
                              {isOv
                                ? <>Total spent is <span className="text-amber-400 font-medium">set to {moneySymbol()}{Number(ov).toFixed(2)}</span> — overriding the formula &amp; logged spend.</>
                                : <>Set the <span className="text-slate-300 font-medium">total spent</span> to override the formula &amp; logged spend.</>}
                            </span>
                            {isOv && (
                              <button type="button"
                                      className="text-[11px] px-2 py-1 rounded-lg border border-edge text-slate-400 hover:text-white disabled:opacity-40"
                                      disabled={ovBusy}
                                      onClick={() => setSpent(true)}>Clear</button>
                            )}
                          </div>
                          <div className="flex gap-1.5">
                            <input className="input !py-1.5 text-sm flex-1"
                                   inputMode="decimal"
                                   placeholder={isOv ? `R${Number(ov).toFixed(2)}` : 'Total spent, e.g. 1250'}
                                   value={ovInput}
                                   onChange={(e) => setOvInput(e.target.value)} />
                            <button type="button"
                                    className="text-xs px-3 rounded-xl border border-amber-400 text-amber-400 bg-amber-400/10 disabled:opacity-40"
                                    disabled={ovBusy || !ovInput.trim() || isNaN(Number(ovInput))}
                                    onClick={() => setSpent(false)}>
                              Set total spent
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    {unit > 0 && (
                      /* A formula target says UPFRONT how to feed it: type a
                         unit count or rands, flip with the chips — everything
                         ADDS to what's already on the expense. */
                      <div className="grid gap-1">
                        <p className="text-[11px] text-slate-400">
                          <span className="text-slate-300 font-medium">
                            {tItem.title}
                          </span>{' '}runs on units of {moneySymbol()}{unit} ({tItem.calculation}).
                          Type <span className="text-amber-400">2</span> to add
                          2 units, or an amount in rands — then pick below.
                        </p>
                        <div className="flex gap-1.5">
                          <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                                    effMode === 'units'
                                      ? 'border-amber-400 text-amber-400 bg-amber-400/10'
                                      : 'border-edge text-slate-500'}`}
                                  onClick={() => setAmtMode('units')}>
                            {amtN > 0
                              ? `+${prospUnits}× unit${prospUnits === 1 ? '' : 's'} `
                                + `of R${unit} = R${(prospUnits * unit).toFixed(2)}`
                              : `UNITS — R${unit} each`}
                          </button>
                          <button className={`flex-1 text-xs py-1.5 rounded-xl border ${
                                    effMode === 'money'
                                      ? 'border-vblue text-vblue bg-vblue/10'
                                      : 'border-edge text-slate-500'}`}
                                  onClick={() => setAmtMode('money')}>
                            {amtN > 0 ? `+R${amtN.toFixed(2)} money` : 'MONEY — rands'}
                          </button>
                        </div>
                        {amtN > 0 && (
                          <p className="text-[10px] text-slate-600">
                            Everything ADDS — nothing is changed. Units advance the
                            paid counter by {prospUnits} (the − / + stepper's number);
                            money adds +{moneySymbol()}{moneyVal.toFixed(2)} to spent so far.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                );
              })() : (
                <>
                  <div className="flex items-center gap-1.5">
                    <input className="input !py-1.5 text-sm flex-1"
                           placeholder="🔎 …or add it to an EXISTING expense — type to search"
                           value={tq} onChange={(e) => setTq(e.target.value)} />
                    <VoiceButton onText={(t) => setTq(t)} title="Search expenses by voice" />
                  </div>
                  {tq.trim() && (
                    <div className="max-h-32 overflow-y-auto grid gap-1">
                      {(budget.items || [])
                        .filter((x) => x.kind === 'expense' && x.source !== 'transfer'
                          && x.title.toLowerCase().includes(tq.trim().toLowerCase()))
                        .slice(0, 6)
                        .map((x) => (
                          <button key={x.id}
                                  className="text-left border border-edge rounded-lg px-3 py-1.5
                                             text-sm hover:border-vblue transition"
                                  onClick={() => { setTarget(x.id); setTq('');
                                                   setRnBump(0); }}>
                            {x.title}
                            <span className="text-[10px] text-slate-500 ml-1.5">
                              {x.calculation ? `= ${x.calculation}` : ''}
                              {x.bank_name ? ` · ${x.bank_name}` : ''}
                            </span>
                          </button>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {/* Amount OR a formula — 200*31, 1200+300*2, 6*8 … the backend's safe
              evaluator computes it, and rate×count formulas get ±unit steppers
              on the month view (pay off one unit at a time). */}
          <div>
            <input className="input" type="text" inputMode="decimal" data-guide="add-amount"
                   placeholder="Amount or formula (e.g. 200*31)"
                   value={form.amount} onChange={(e) => set('amount', e.target.value)} />
            <p className="text-[11px] text-slate-500 mt-1">
              A number (<span className="text-slate-400">450</span>) or a formula
              (<span className="text-slate-400">200*31</span>,{' '}
              <span className="text-slate-400">1200+300*2</span>) — formulas get
              ±unit steppers on the month so you can pay off one unit at a time.
            </p>
            {(() => {
              // Live plain-English read-back so you APPROVE the amount before saving:
              // a FIXED number, a single rate×count (which drives the ±unit steppers),
              // or a COMPOUND formula (safe-evaluated; the first rate×count term sets
              // the per-unit rate). Covers every shape, not just "a*b".
              const fmtT = (n) => 'R' + Number(n).toFixed(2).replace(/\.00$/, '');
              const raw = String(form.amount || '').replace(/[rR\s]/g, '')
                .replace(/[x×]/gi, '*').replace(',', '.');
              if (!raw) return null;
              const hasOp = /[+\-*/]/.test(raw.slice(1));   // an operator beyond a leading sign
              if (!hasOp) {
                const n = Number(raw);
                if (!Number.isFinite(n)) return null;
                return (
                  <div className="mt-1.5 text-[11px] text-vblue border border-vblue/30 rounded-lg px-2.5 py-1.5">
                    Fixed amount — <span className="font-medium">{fmtT(n)}</span> budgeted this month.
                    To pay it off bit by bit instead, type a formula like <span className="font-medium">200*31</span>.
                  </div>
                );
              }
              if (!/^[0-9+\-*/().]+$/.test(raw)) return null;   // a formula must be pure arithmetic
              let total = null;
              try { total = Function('"use strict";return(' + raw + ')')(); } catch { total = null; }
              if (total == null || !Number.isFinite(total)) return null;
              const term = raw.split('+').find((t) => t.includes('*'));
              const nums = term ? (term.match(/\d+(?:\.\d+)?/g) || []) : [];
              const rate = nums.length ? Math.max(...nums.map(Number)) : 0;
              const simple = /^(\d+(?:\.\d+)?)\*(\d+(?:\.\d+)?)$/.test(raw);
              return (
                <div className="mt-1.5 text-[11px] text-vblue border border-vblue/30 rounded-lg px-2.5 py-1.5">
                  Reads as <span className="font-medium">{raw}</span> ={' '}
                  <span className="font-medium">{fmtT(total)}</span> this month.
                  {rate > 0 && <> Each ±unit step pays <span className="font-medium">{moneySymbol()}{rate}</span>{simple ? '' : ' (from the first rate×count part)'}.</>}
                  {' '}Approve if that reads right, or edit above.
                </div>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className="input" value={form.bank} onChange={(e) => set('bank', e.target.value)} data-guide="add-bank"
                    title="Leave empty and Vault guesses from your history — same category or merchant, else your receiving bank">
              <option value="">Bank (AI guesses)</option>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name}</option>)}
            </select>
            <div className="relative">
              <CategoryPicker categories={categories} value={form.category || null}
                              title={form.title} kind={kind}
                              key={`addcat:${form.category}`}
                              onPicked={(id) => { set('category', id ? String(id) : '');
                                                  setAiCat(false); }} />
              {aiCat && (
                <span className="absolute -top-2 right-2 text-[9px] uppercase tracking-wide
                                 bg-panel px-1 text-vpurple">AI picked</span>
              )}
            </div>
          </div>
          <input className="input" data-guide="add-note" placeholder="Note (AI fills it — edit freely)"
                 value={form.description} onChange={(e) => set('description', e.target.value)} />

          <div className="flex gap-2" data-guide="add-toggles">
            <Toggle on={form.completed} onClick={() => set('completed', !form.completed)}
                    labelOn={kind === 'income' ? 'Already received' : 'Already paid'}
                    labelOff={kind === 'income' ? 'Still expected' : 'Still to pay'} />
            <Toggle on={recurring} onClick={() => setRecurring(!recurring)}
                    labelOn="Recurring — every month" labelOff="Just this month" purple />
          </div>
          {recurring && kind === 'expense' && (
            <div className="rounded-xl border border-vpurple/30 bg-vpurple/5 p-3 mt-1">
              <button type="button"
                      className="text-[11px] uppercase tracking-wide text-vpurple flex items-center gap-1"
                      onClick={() => setAdvRepeat((o) => !o)}>
                {advRepeat ? '▾' : '▸'} ⚙ Advanced repeat
              </button>
              {advRepeat && (
                <div className="grid gap-3 mt-3">
                  <div className="flex flex-wrap gap-1.5">
                    {[['none', 'One-off'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setCadence(v)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition ${cadence === v ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400 hover:border-slate-400'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  {cadence !== 'none' && (
                    <label className="text-xs text-slate-400">R per unit
                      <input className="input mt-1" type="number" inputMode="decimal" placeholder="e.g. 200"
                             value={unitRate} onChange={(e) => setUnitRate(e.target.value)} />
                    </label>
                  )}
                  {cadence === 'daily' && (
                    <label className="text-xs text-slate-400">Times per day
                      <input className="input mt-1" type="number" min="1" max="12"
                             value={timesPerDay} onChange={(e) => setTimesPerDay(e.target.value)} />
                    </label>
                  )}
                  {cadence === 'weekly' && (
                    <div>
                      <div className="text-xs text-slate-400 mb-1.5">On these days</div>
                      <div className="flex flex-wrap gap-1.5">
                        {[[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']].map(([d, l]) => {
                          const on = dow.includes(d);
                          return (
                            <button key={d} type="button"
                                    onClick={() => setDow((sd) => on ? sd.filter((x) => x !== d) : [...sd, d])}
                                    className={`text-xs px-2.5 py-1.5 rounded-lg border transition ${on ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400 hover:border-slate-400'}`}>
                              {l}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {cadence === 'monthly' && (
                    <label className="text-xs text-slate-400">Day of month (optional)
                      <input className="input mt-1" type="number" min="1" max="28" placeholder="e.g. 1"
                             value={expectedDay} onChange={(e) => setExpectedDay(e.target.value)} />
                    </label>
                  )}
                  <p className="text-[10px] text-slate-500 leading-relaxed">{repeatSummary()}</p>
                </div>
              )}
            </div>
          )}
          {kind === 'expense' && (
            <div className="mt-1" data-guide="add-tax">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.tax_deductible}
                       onChange={(e) => set('tax_deductible', e.target.checked)} />
                Tax-deductible (SARS)
              </label>
              {form.tax_deductible && (
                <select className="input mt-2" value={form.tax_bucket || ''}
                        onChange={(e) => set('tax_bucket', e.target.value)}>
                  <option value="">Pick a SARS bucket</option>
                  <option value="travel">Travel</option>
                  <option value="home_office">Home office</option>
                  <option value="donations">Donations</option>
                  <option value="medical">Medical</option>
                  <option value="retirement">Retirement</option>
                  <option value="business">Business</option>
                </select>
              )}
            </div>
          )}
          {kind === 'income' && (
            <Toggle on={isSalary} onClick={() => setIsSalary(!isSalary)}
                    labelOn="This is my salary — it starts the new month"
                    labelOff="Not my salary" purple />
          )}
          {kind === 'income' && masterLine && (
            /* A choice, never a rule: money moved from the master deducts
               its bank when this income is (or arrives) received; new money
               from outside leaves the master alone. Needs a landing bank
               that isn't the master's — the hint says so until then. */
            offMaster ? (
              <Toggle on={fromMaster} onClick={() => setFromMaster(!fromMaster)}
                      labelOn={`From ★ ${masterLine.bank_name || 'master'} — deduct it there`}
                      labelOff="New money — master untouched" purple />
            ) : (
              <p className="text-[11px] text-slate-500">
                {form.bank
                  ? `Landing on ★ ${masterLine.bank_name || 'the master'} itself — `
                    + 'counts globally, nothing to pair.'
                  : `Pick the bank it lands in — landing off ★ ${
                      masterLine.bank_name || 'the master'} offers the `
                    + 'deduct-from-master choice.'}
              </p>
            )
          )}

          {kind === 'expense' && (
            /* OPTIONAL location. New expense → its HOME shop. Adding to an
               EXISTING expense → where THIS spend happened (defaults to the
               expense's own shop; pick another to log this entry elsewhere —
               e.g. a Coffee spend that happened at a different café). */
            <div className="border border-edge rounded-xl p-2.5 grid gap-1.5 min-w-0">
              {(!shopOpen && !place) ? (
                <button type="button" className="text-sm text-slate-400 hover:text-vblue text-left"
                        onClick={openShop}>
                  {target ? (() => {
                    const tp = savedPlaces.find((sp) => sp.id === (budget.items || [])
                      .find((x) => x.id === target)?.place);
                    return (<>📍 Log this spend at a shop
                      <span className="text-[11px] text-slate-600"> — defaults to {tp ? tp.name : 'no shop'}; pick another for THIS entry</span></>);
                  })() : (<>📍 Also link it to a shop
                    <span className="text-[11px] text-slate-600"> — optional; shows on the map &amp; enables nearby reminders</span></>)}
                </button>
              ) : place ? (
                <>
                <div className="grid gap-1.5">
                  <div className="flex items-center gap-2">
                    <input className="input !py-1.5 text-sm flex-1"
                           title="Name this place — edit it to whatever you call it"
                           value={place.name}
                           onChange={(e) => setPlace({ ...place, name: e.target.value.slice(0, 60) })}
                           placeholder="Name this place" />
                    {place.distance_km != null && (
                      <span className="text-[11px] text-vpurple shrink-0">{place.distance_km} km</span>
                    )}
                    <button type="button"
                            className="w-6 h-6 rounded-full border border-edge text-slate-400 hover:text-white text-xs shrink-0"
                            onClick={() => setPlace(null)}>✕</button>
                  </div>
                  {place.address ? (
                    <span className="text-[11px] text-slate-500 truncate">📍 {place.address}</span>
                  ) : null}
                  <button type="button" className="text-[11px] text-vblue hover:underline text-left"
                          onClick={() => { setPlace(null); openShop(); }}>Pick a different shop</button>
                </div>
                <PinPreview lat={place.lat} lng={place.lng} />
                </>
              ) : (
                <>
                  <div className="flex gap-1.5">
                    <input className="input !py-1.5 text-sm flex-1"
                           placeholder="🔎 Shop name or address (nearest first)"
                           value={shopQ} onChange={(e) => setShopQ(e.target.value)}
                           onKeyDown={(e) => e.key === 'Enter' && searchShops()} />
                    <button type="button"
                            className="text-xs px-2.5 rounded-xl border border-edge text-slate-300 hover:border-vblue"
                            disabled={shopBusy} onClick={searchShops}>{shopBusy ? '…' : 'Find'}</button>
                    <VoiceButton onText={(t) => setShopQ(t)} title="Say the shop name" />
                  </div>
                  <button type="button"
                          className="text-left border border-vblue/50 rounded-lg px-2.5 py-1.5 text-sm text-vblue hover:bg-vblue/10 w-full"
                          onClick={async () => {
                            let pt = pos;
                            if (!pt) {
                              pt = await new Promise((res) => {
                                if (!navigator.geolocation) return res(null);
                                navigator.geolocation.getCurrentPosition(
                                  (g) => res({ lat: g.coords.latitude, lng: g.coords.longitude }),
                                  () => res(null),
                                  { timeout: 10000, maximumAge: 600000, enableHighAccuracy: false });
                              });
                              if (pt) setPos(pt);
                            }
                            if (!pt) { setError('Could not get your location — allow location access.'); return; }
                            const fallback = { name: 'My current location', address: '',
                              lat: pt.lat, lng: pt.lng, source: 'address' };
                            try {
                              const d = await api('/budget/places/reverse_geocode/',
                                { method: 'POST', body: { lat: pt.lat, lng: pt.lng } });
                              setPlace(d && d.lat != null ? d : fallback);
                            } catch { setPlace(fallback); }
                          }}>
                    📍 Use my current location as the pin
                  </button>
                  {savedPlaces
                    .filter((sp) => { const q = shopQ.trim().toLowerCase();
                      return !q || (sp.name || '').toLowerCase().includes(q)
                        || (sp.address || '').toLowerCase().includes(q); })
                    .slice(0, shopQ.trim() ? 50 : 8)
                    .map((sp) => (
                      <button key={`sp${sp.id}`} type="button"
                              className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vblue w-full min-w-0 block truncate"
                              onClick={() => setPlace(sp)}>
                        📍 {sp.name}
                        {sp.address ? <span className="text-[11px] text-slate-500"> · {sp.address}</span> : null}
                      </button>
                  ))}
                  {shopResults.map((c, i) => (
                    <button key={`sr${i}`} type="button"
                            className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vpurple w-full min-w-0 block"
                            onClick={() => setPlace(c)}>
                      <span className="font-medium block truncate">{c.name}
                        <span className={`ml-1.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded ${
                          c.source === 'address' ? 'text-amber-400 border border-amber-400/40'
                                                 : 'text-vpurple border border-vpurple/40'}`}>
                          {c.source === 'address' ? 'address' : 'shop'}
                        </span>
                      </span>
                      {c.distance_km != null && (
                        <span className="text-[11px] text-vpurple"> · {c.distance_km} km</span>
                      )}
                      <span className="block text-[11px] text-slate-500 truncate">{c.address}</span>
                    </button>
                  ))}
                  <button type="button" className="text-[11px] text-slate-600 text-left"
                          onClick={() => { setShopOpen(false); setShopResults([]); }}>
                    skip — no shop
                  </button>
                </>
              )}
            </div>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button className="btn-primary" data-guide="add-save"
                  onClick={kind === 'expense' && target ? applyToExisting : save}
                  disabled={busy || (kind === 'expense' && target
                    ? (!form.amount || (!tSpend && !tReplace))
                    : !form.title)}>
            {busy ? 'Saving…'
                  : kind === 'expense' && target ? (tReplace ? 'Replace & mark done' : 'Apply to the expense')
                  : recurring ? 'Save & make recurring'
                  : kind === 'income' ? 'Save income' : 'Save expense'}
          </button>
        </div>

        <button className="mt-4 text-sm text-slate-500 hover:text-slate-300" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Toggle({ on, onClick, labelOn, labelOff, purple }) {
  return (
    <button onClick={onClick}
            className={`flex-1 text-sm px-3 py-2 rounded-xl border transition ${
              on ? (purple ? 'border-vpurple bg-vpurple/15' : 'border-vblue bg-vblue/15')
                 : 'border-edge text-slate-400'}`}>
      {on ? labelOn : labelOff}
    </button>
  );
}
